import React, {
	useEffect,
	useMemo,
	useState,
	useRef,
} from 'react';
import classNames from 'classnames';
import { VideoLayouts, LAYOUTS_BY_SIZE } from '../../utils/video-layout';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import ModalLarge from '../general-components/modal-large';
import SelectGroup, { SelectGroupOption } from '../general-components/select-group';
import SlidingMenu, { SlidingMenuOption } from '../general-components/sliding-menu';
import LayoutSelector from '../general-components/layout-selector';

interface Props {
	open: boolean;
	close: () => void;
	onApply: Function;
	currentLayout?: string;
}

const presenterOptions: SelectGroupOption[] = [
	{ label: '1-8', value: 0 },
	{ label: '1', value: 1 },
	{ label: '2', value: 2 },
	{ label: '3', value: 3 },
	{ label: '4', value: 4 },
	{ label: '5', value: 5 },
	{ label: '6', value: 6 },
	{ label: '7', value: 7 },
	{ label: '8', value: 8 },
];

const filterOptions: SlidingMenuOption[] = [
	{ label: 'All' },
	{ label: 'Presenters' },
	{ label: 'Media' },
];

export default function LayoutModal(props: Props) {
	const {
		open,
		close,
		onApply,
		currentLayout = '',
	} = props;

	const [currentTab, setCurrentTab] = useState<SlidingMenuOption | null>(null);
	const [filteredLayouts, setFilteredLayouts] = useState<VideoLayouts[]>([...LAYOUTS_BY_SIZE]);
	const [filteredPresenterOpts, setFilteredPresenterOpts] = useState<SelectGroupOption[]>(
		presenterOptions,
	);
	const [numberOfPresenters, setNumberOfPresenters] = useState<number>(presenterOptions[0].value);
	const [rowWidth, setRowWidth] = useState<number>(0);

	const layoutWrapperRef = useRef<HTMLDivElement | null>(null);
	const layoutThumbnailSize = 190 + 16; // width of layout selector buttons + right margin

	const resizeObserver = new ResizeObserver((entries) => {
		const newRowWidth = entries[0].contentRect.width - 24; // subtract left margin from row width
		setRowWidth(newRowWidth);
	});

	useEffect(() => {
		if (numberOfPresenters === presenterOptions[0].value) {
			// when changing filters and all is selected, reset the scroll position
			// of each row as the number of layouts in a row might change as well
			const newRows = new Array(filteredPresenterOpts.length - 1).fill(0);
			const rowScrollsToReset = document.getElementsByClassName('layout-row');
			newRows.forEach((_, rowIndex: number) => rowScrollsToReset?.[rowIndex]?.scrollTo({ top: 0, left: 0, behavior: 'smooth' }));
		}
	}, [filteredPresenterOpts, numberOfPresenters]);

	useEffect(() => {
		if (layoutWrapperRef.current) {
			resizeObserver.observe(layoutWrapperRef.current);
		}

		return () => {
			if (layoutWrapperRef) {
				resizeObserver.disconnect();
			}
		};
	}, [layoutWrapperRef, resizeObserver]);

	function scroll(index: number, right: boolean) {
		const elementToScroll = document.getElementById(`row-${index}`);
		if (!elementToScroll) return;
		if (right) {
			elementToScroll.scrollBy({ top: 0, left: layoutThumbnailSize, behavior: 'smooth' }); // move row one layout button to the right
		} else {
			elementToScroll.scrollBy({ top: 0, left: -layoutThumbnailSize, behavior: 'smooth' }); // move row one layout button to the left
		}
	}

	const changeTab = (option: SlidingMenuOption) => {
		setCurrentTab(option);
		if (option.label !== 'All') {
			// when the tab is changed to anything other than all, filter the layouts and dropdown options
			const newFilteredLayouts = LAYOUTS_BY_SIZE.map((configs: VideoLayouts) => {
				const copiedConfigs = { ...configs };
				copiedConfigs.layouts = option.label === 'Presenters'
					? copiedConfigs.layouts.filter((layout: string) => !layout.includes('media'))
					: copiedConfigs.layouts.filter((layout: string) => layout.includes('media'));
				return copiedConfigs;
			}).filter((configs: VideoLayouts) => configs.layouts.length !== 0);
			setFilteredLayouts(newFilteredLayouts);
			const updatedOptions = presenterOptions.filter(
				(presentedOption: SelectGroupOption) => newFilteredLayouts.some(
					(configs: VideoLayouts) => configs.presenters === presentedOption.value,
				),
			);
			setFilteredPresenterOpts([presenterOptions[0], ...updatedOptions]);
		} else {
			// if the tab is changed to all, reset the filters to show all layouts and dropdown options
			setFilteredLayouts(LAYOUTS_BY_SIZE);
			setFilteredPresenterOpts(presenterOptions);
		}
	};

	const maxLayoutsShown = useMemo(
		// number of layouts we can comfortably show in each row
		() => Math.floor(rowWidth / layoutThumbnailSize),
		[rowWidth, layoutThumbnailSize],
	);

	const layouts = useMemo(() => {
		if (numberOfPresenters === 0) {
			// all presenter/media options
			return filteredLayouts.map((configs: VideoLayouts, mainIndex: number) => (
					<div className="layout-row-container" key={mainIndex}>
						<label>{configs?.presenters}</label>
						<div className="layout-row" id={`row-${mainIndex}`}>
							{configs?.layouts.map((layout: string, subIndex: number) => (
								<LayoutSelector
									key={subIndex}
									onClick={() => {
										onApply(layout, configs?.presenters);
										close();
									}}
									selected={currentLayout === layout}
									type={layout}
									number={configs.presenters}
									layoutIndex={subIndex}
									displayIcon={false}
									hasPictureInPicture={layout.includes('picture-in-picture')}
								/>
							))}
							{configs?.layouts.length > maxLayoutsShown && (
								<button className="scroll-arrow left" onMouseDown={() => scroll(mainIndex, false)}>
									<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={10} color={COLORS.BLACK} />
								</button>
							)}
							{configs?.layouts.length > maxLayoutsShown && (
								<button className="scroll-arrow" onMouseDown={() => scroll(mainIndex, true)}>
									<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={10} color={COLORS.BLACK} />
								</button>
							)}
						</div>
					</div>
			));
		}
		// individual presenter/media options
		const configs = filteredLayouts.find(
			(option: VideoLayouts) => option.presenters === numberOfPresenters,
		);
		if (configs && configs?.layouts.length > 0) {
			return (
				<div className="layout-row-container">
					<label>{configs?.presenters}</label>
					<div className="layout-row">
						{configs?.layouts.map((layout: string, index: number) => (
							<LayoutSelector
								key={index}
								onClick={() => {
									onApply(layout, configs?.presenters);
									close();
								}}
								selected={currentLayout === layout}
								type={layout}
								number={configs.presenters}
								layoutIndex={index}
								displayIcon={false}
								hasPictureInPicture={layout.includes('picture-in-picture')}
							/>
						))}
					</div>
				</div>
			);
		}
		// if there are no layouts for this option
		return (
			<p className="no-layouts">Currently there are no supported layouts for this configuration.</p>
		);
	}, [filteredPresenterOpts,
		numberOfPresenters,
		currentTab,
		currentLayout,
		maxLayoutsShown,
		onApply,
	]);

	return (
		<ModalLarge
			open={open}
			close={close}
			className="layout-modal"
			closeIfClickOutside={true}
		>
			<div className="layout-modal-top">
				<div className="layout-modal-header-row">
					<label>Shot layout</label>
				</div>
				<div className="layout-modal-tabs-row">
					<SlidingMenu
						options={filterOptions}
						selectedOption={currentTab}
						callback={changeTab}
						fullWidthDivider={true}
					/>
					<div className="layout-modal-amount-dropdown">
						<SelectGroup
							onChange={setNumberOfPresenters}
							options={filteredPresenterOpts}
							label={'Amount of presenters'}
							selected={
								presenterOptions.find((option) => option.value === numberOfPresenters)
								|| presenterOptions[0]
							}
							placeholder={'Choose your Speaker'}
						/>
					</div>
				</div>
			</div>
			<div className={classNames('layout-modal-main', numberOfPresenters > 0 && 'single')}>
				<div className="layout-wrapper" ref={layoutWrapperRef}>
					{layouts}
				</div>
			</div>
		</ModalLarge>
	);
}
